import { z } from 'zod';

/// Quanto do adiantamento fica com o estabelecimento: centésimos de ponto percentual no percentual
/// (10000 = 100%) ou centavos no valor fixo. Nunca passa do adiantamento pago (ADR 0071, decisão 7).
export const schedulingRetentionSchema = z
  .object({
    mode: z.enum(['PERCENTAGE', 'FIXED']),
    amount: z.number().int().min(0).max(1_000_000_000),
  })
  .strict();

/// O que acontece com o que não fica retido: o cliente escolhe entre devolução e crédito, como no
/// ADR 0066, ou a regra fixa um dos dois. A validade do crédito chega na fatia 2b (ADR 0071,
/// decisão 21); até lá ela fica nula e a regra que a preenche é recusada.
const remainderShape = {
  remainder: z.enum(['CHOICE', 'REFUND', 'CREDIT']),
  creditValidityDays: z.number().int().min(1).max(3650).nullable(),
};

/// Uma faixa vale para o cancelamento feito com menos de `withinHours` horas até o início. Entre
/// várias faixas que alcançam o mesmo instante, vale a mais curta.
export const schedulingCancellationTierSchema = z
  .object({
    withinHours: z.number().int().min(1).max(8760),
    retention: schedulingRetentionSchema,
    ...remainderShape,
  })
  .strict();

export const schedulingNoShowRuleSchema = z
  .object({ retention: schedulingRetentionSchema, ...remainderShape })
  .strict();

/// Remarcar é gratuito até `freeHours` antes do início. Depois disso, o portal manda falar com o
/// estabelecimento, retém parte do adiantamento ou deixa remarcar livremente. O limite é opcional.
export const schedulingRescheduleRuleSchema = z
  .object({
    freeHours: z.number().int().min(0).max(8760),
    late: z.enum(['CONTACT', 'RETAIN', 'FREE']),
    retention: schedulingRetentionSchema.nullable(),
    limit: z.number().int().min(1).max(20).nullable(),
  })
  .strict();

/// Política de cancelamento e remarcação (ADR 0071). Sem faixas, sem falta e sem regra de
/// remarcação, vale o ADR 0066: devolução integral ou crédito, e remarcação livre.
export const schedulingCancellationPolicySchema = z
  .object({
    tiers: z.array(schedulingCancellationTierSchema).max(5),
    noShow: schedulingNoShowRuleSchema.nullable(),
    reschedule: schedulingRescheduleRuleSchema.nullable(),
  })
  .strict();

export type SchedulingRetention = z.infer<typeof schedulingRetentionSchema>;
export type SchedulingCancellationTier = z.infer<typeof schedulingCancellationTierSchema>;
export type SchedulingNoShowRule = z.infer<typeof schedulingNoShowRuleSchema>;
export type SchedulingRescheduleRule = z.infer<typeof schedulingRescheduleRuleSchema>;
export type SchedulingCancellationPolicy = z.infer<typeof schedulingCancellationPolicySchema>;

/// De onde vem a regra em vigor: a cascata empresa → unidade → serviço (ADR 0071, decisão 1).
export type SchedulingCancellationSource = 'COMPANY' | 'UNIT' | 'SERVICE';

export type SchedulingCancellationSettings = {
  company: SchedulingCancellationPolicy;
  /// Unidade com regra própria; ausente, usa a da empresa.
  units: Record<string, SchedulingCancellationPolicy>;
  /// Serviço com regra própria, em todas as unidades; ausente, usa a da unidade.
  services: Record<string, SchedulingCancellationPolicy>;
};

/// O retrato que o agendamento guarda: a regra aceita, a frase lida e o nível de onde ela veio.
export const schedulingCancellationSnapshotSchema = z
  .object({
    policy: schedulingCancellationPolicySchema,
    summary: z.string().min(1).max(4000),
    source: z.enum(['COMPANY', 'UNIT', 'SERVICE']),
  })
  .strict();
export type SchedulingCancellationSnapshot = z.infer<typeof schedulingCancellationSnapshotSchema>;

export const defaultSchedulingCancellationPolicy: SchedulingCancellationPolicy = Object.freeze({
  tiers: [],
  noShow: null,
  reschedule: null,
});

export function resolveSchedulingCancellationPolicy(
  settings: SchedulingCancellationSettings,
  businessUnitId: string,
  serviceId?: string,
): { policy: SchedulingCancellationPolicy; source: SchedulingCancellationSource } {
  const service = serviceId ? settings.services[serviceId] : undefined;
  if (service) return { policy: service, source: 'SERVICE' };
  const unit = settings.units[businessUnitId];
  if (unit) return { policy: unit, source: 'UNIT' };
  return { policy: settings.company, source: 'COMPANY' };
}

/// Os problemas que impedem salvar a regra, em linguagem de quem configura.
export function reviewSchedulingCancellationPolicy(policy: SchedulingCancellationPolicy): string[] {
  const errors: string[] = [];
  const hours = policy.tiers.map((tier) => tier.withinHours);
  if (hours.some((value) => !Number.isInteger(value) || value < 1))
    errors.push('Informe em horas inteiras quando cada faixa começa.');
  if (new Set(hours).size !== hours.length)
    errors.push('Cada faixa de cancelamento precisa de uma antecedência diferente.');
  const retentions = [
    ...policy.tiers.map((tier) => tier.retention),
    ...(policy.noShow ? [policy.noShow.retention] : []),
    ...(policy.reschedule?.late === 'RETAIN' && policy.reschedule.retention
      ? [policy.reschedule.retention]
      : []),
  ];
  if (retentions.some((retention) => retention.amount <= 0))
    errors.push('Informe uma retenção maior que zero.');
  if (retentions.some((retention) => retention.mode === 'PERCENTAGE' && retention.amount > 10000))
    errors.push('A retenção não pode superar 100% do adiantamento.');
  if (policy.reschedule?.late === 'RETAIN' && !policy.reschedule.retention)
    errors.push('Informe quanto fica retido ao remarcar fora do prazo.');
  if (
    [...policy.tiers, ...(policy.noShow ? [policy.noShow] : [])].some(
      (rule) => rule.creditValidityDays !== null,
    )
  )
    errors.push('A validade do crédito ainda não está disponível; deixe o crédito sem validade.');
  return errors;
}

/// A frase-resumo em linguagem leiga (ADR 0071, decisão 9). É gerada da própria regra: a mesma
/// frase aparece na configuração, ao cliente antes do pedido e no retrato do agendamento.
export function describeSchedulingCancellationPolicy(
  policy: SchedulingCancellationPolicy,
  currency = 'BRL',
): string {
  const tiers = [...policy.tiers].sort((left, right) => right.withinHours - left.withinHours);
  const sentences: string[] = [];
  if (!tiers.length)
    sentences.push(
      'Você pode cancelar antes do atendimento e escolher entre a devolução integral do adiantamento e o crédito.',
    );
  else {
    sentences.push(
      `Cancelando com ${hoursOf(tiers[0]!.withinHours)} ou mais de antecedência, você escolhe entre a devolução integral do adiantamento e o crédito.`,
    );
    tiers.forEach((tier, index) => {
      const next = tiers[index + 1];
      const window = next
        ? `Com menos de ${hoursOf(tier.withinHours)} e pelo menos ${hoursOf(next.withinHours)}`
        : `Com menos de ${hoursOf(tier.withinHours)}`;
      sentences.push(`${window}, ${retained(tier, currency)}.`);
    });
  }
  if (policy.noShow) sentences.push(`Se você faltar, ${retained(policy.noShow, currency)}.`);
  const reschedule = policy.reschedule;
  if (reschedule) {
    if (reschedule.late === 'FREE' || reschedule.freeHours === 0)
      sentences.push('Você pode remarcar sem custo a qualquer momento antes do atendimento.');
    else {
      sentences.push(`Remarcar é gratuito até ${hoursOf(reschedule.freeHours)} antes do início.`);
      sentences.push(
        reschedule.late === 'CONTACT'
          ? 'Depois disso, fale com o estabelecimento para remarcar.'
          : `Depois disso, remarcar retém ${retentionOf(reschedule.retention!, currency)} e o restante segue para o novo horário.`,
      );
    }
    if (reschedule.limit)
      sentences.push(
        reschedule.limit === 1
          ? 'Cada agendamento pode ser remarcado uma vez.'
          : `Cada agendamento pode ser remarcado até ${String(reschedule.limit)} vezes.`,
      );
  }
  if (tiers.length || policy.noShow || reschedule?.late === 'RETAIN')
    sentences.push(
      'A retenção só alcança o adiantamento já pago; sem adiantamento, nada é cobrado.',
    );
  sentences.push('Nenhuma tarifa é descontada do valor devolvido.');
  return sentences.join(' ');
}

export type SchedulingCancellationOutcome = {
  /// A faixa que alcançou o cancelamento; nula fora de todas, quando nada fica retido.
  tier: SchedulingCancellationTier | null;
  retainedCents: number;
  /// O destino do que não fica retido; `CHOICE` deixa o cliente escolher.
  remainder: 'CHOICE' | 'REFUND' | 'CREDIT';
};

/// O que a regra retém num cancelamento pelo cliente (ADR 0071, decisões 2 e 7). A antecedência é
/// contada do instante da ação até o início; entre as faixas que alcançam esse instante vale a mais
/// curta. A retenção nunca passa do adiantamento confirmado e o percentual arredonda a favor do
/// cliente.
export function applySchedulingCancellationPolicy(
  policy: SchedulingCancellationPolicy,
  input: { startsAt: Date; at: Date; paidCents: number },
): SchedulingCancellationOutcome {
  const hoursLeft = (input.startsAt.getTime() - input.at.getTime()) / 3_600_000;
  const tier =
    [...policy.tiers]
      .filter((one) => hoursLeft < one.withinHours)
      .sort((left, right) => left.withinHours - right.withinHours)[0] ?? null;
  const paid = Math.max(0, input.paidCents);
  if (!tier) return { tier: null, retainedCents: 0, remainder: 'CHOICE' };
  const retainedCents =
    tier.retention.mode === 'PERCENTAGE'
      ? Math.floor((paid * Math.min(tier.retention.amount, 10000)) / 10000)
      : Math.min(tier.retention.amount, paid);
  return { tier, retainedCents, remainder: tier.remainder };
}

function retained(
  rule: { retention: SchedulingRetention; remainder: 'CHOICE' | 'REFUND' | 'CREDIT' },
  currency: string,
): string {
  const whole = rule.retention.mode === 'PERCENTAGE' && rule.retention.amount >= 10000;
  if (whole) return 'o adiantamento inteiro fica com o estabelecimento';
  const rest =
    rule.remainder === 'REFUND'
      ? 'o restante é devolvido'
      : rule.remainder === 'CREDIT'
        ? 'o restante vira crédito'
        : 'o restante volta como devolução ou crédito, à sua escolha';
  return `${retentionOf(rule.retention, currency)} fica com o estabelecimento e ${rest}`;
}

function retentionOf(retention: SchedulingRetention, currency: string): string {
  if (retention.mode === 'PERCENTAGE')
    return retention.amount >= 10000
      ? 'o adiantamento inteiro'
      : `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(retention.amount / 100)}% do adiantamento`;
  return `até ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format(retention.amount / 100)} do adiantamento`;
}

function hoursOf(hours: number): string {
  return hours === 1 ? '1 hora' : `${String(hours)} horas`;
}
